import { Component, OnInit, Input } from "@angular/core";
import {
  DataService,
  Dialog,
  NotificationService,
  SharedModule,
  ModalService,
} from "@bb-vendure/admin-ui/core";
import {
  CheckBulkAddFreeGiftMutation,
  CheckBulkAddFreeGiftDocument,
  BulkAddFreeGiftPackageReviewMutation,
  BulkAddFreeGiftPackageReviewDocument,
  GetProductFreeGiftSelectionQuery,
  GetProductFreeGiftSelectionDocument,
  PackageReviewsQuery,
} from "src/generated-gql/graphql";
import { FormBuilder } from "@angular/forms";
import { take } from "rxjs";

@Component({
  selector: "vdr-bulk-add-free-gift-dialog",
  templateUrl: "./bulk-add-free-gift-dialog.component.html",
  styleUrls: ["./bulk-add-free-gift-dialog.component.scss"],
  standalone: true,
  imports: [SharedModule],
})
export class BulkAddFreeGiftDialogComponent implements Dialog<boolean>, OnInit {
  resolveWith: (result?: boolean) => void;

  @Input()
  items: PackageReviewsQuery["packageReviews"]["items"];

  loading = false;
  checking = false;
  checkResult: CheckBulkAddFreeGiftMutation["checkBulkAddFreeGift"] | null =
    null;
  selectedProductId: string | null = null;
  productName: string | null = null;
  freeGiftSelection:
    | GetProductFreeGiftSelectionQuery["freeGiftSelectionFromProductId"]
    | null = null;
  invalidOrders: any[] = [];

  // New properties for multi-product support
  availableProducts: Array<{
    id: string;
    name: string;
    freeGiftSelection: any;
    loading: boolean;
    hasConditions: boolean;
  }> = [];
  allProductsLoaded = false;

  // For bulk apply mode UI
  expandedProductId: string | null = null;

  detailForm = this.formBuilder.group({
    productId: [""],
  });

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
    private formBuilder: FormBuilder,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.loadAllProductsInfo();
  }

  async loadAllProductsInfo() {
    this.loading = true;

    try {
      // Extract all unique product IDs from selected package reviews
      const allProductIds = new Set<string>();

      this.items.forEach((item) => {
        if (!item.lines) {
          return;
        }

        const nonFreeGiftLines = item.lines.filter((line: any) => {
          const isFreeGift =
            line.productVariant?.product?.customFields?.freeGifts;
          return !isFreeGift;
        });

        nonFreeGiftLines.forEach((line: any) => {
          const productId = line.productVariant?.productId;
          if (productId) {
            allProductIds.add(productId);
          }
        });
      });

      // Initialize availableProducts array
      this.availableProducts = Array.from(allProductIds).map((id) => ({
        id,
        name: "",
        freeGiftSelection: null,
        loading: true,
        hasConditions: false,
      }));

      // Load information for each product in parallel
      const loadPromises = this.availableProducts.map(async (product) => {
        try {
          const result = await this.dataService
            .query(GetProductFreeGiftSelectionDocument, {
              productId: product.id,
            })
            .single$.pipe(take(1))
            .toPromise();

          if (result) {
            product.name = result.product?.name || `Product ${product.id}`;
            product.freeGiftSelection = result.freeGiftSelectionFromProductId;
            product.hasConditions = !!(
              product.freeGiftSelection?.conditions &&
              product.freeGiftSelection.conditions.length > 0
            );
          } else {
            product.name = `Product ${product.id} (Not Found)`;
          }
        } catch (error) {
          product.name = `Product ${product.id} (Error)`;
        } finally {
          product.loading = false;
        }
      });

      // Wait for all products to load
      await Promise.all(loadPromises);

      // Sort products: those with conditions first
      this.availableProducts.sort((a, b) => {
        if (a.hasConditions && !b.hasConditions) return -1;
        if (!a.hasConditions && b.hasConditions) return 1;
        return a.name.localeCompare(b.name);
      });

      // Auto-select behavior:
      // - If in bulk apply mode (multiple products with conditions), don't auto-select
      // - If only one product has conditions, auto-select it for traditional mode
      const productsWithConditions = this.availableProducts.filter(
        (p) => p.hasConditions
      );
      if (productsWithConditions.length === 1 && !this.isBulkApplyMode) {
        this.selectProduct(productsWithConditions[0]);
      }
    } catch (error) {
      this.notificationService.error("Failed to load products information");
    } finally {
      this.loading = false;
      this.allProductsLoaded = true;
    }
  }

  selectProduct(product: any) {
    this.selectedProductId = product.id;
    this.productName = product.name;
    this.freeGiftSelection = product.freeGiftSelection;
    this.detailForm.patchValue({ productId: product.id });

    // Reset previous check results
    this.checkResult = null;
    this.invalidOrders = [];
  }

  async checkBulkOperation() {
    // For single package review with multiple available products, check all products
    if (this.isBulkApplyMode) {
      return this.checkAllAvailableProducts();
    }

    if (!this.selectedProductId) {
      this.notificationService.error("Please select a product");
      return;
    }

    // Check how many packages contain the selected product
    const packagesWithSelectedProduct = this.getPackagesContainingProduct(
      this.selectedProductId
    );
    if (packagesWithSelectedProduct.length === 0) {
      this.notificationService.error(
        "Selected product is not found in any of the selected packages"
      );
      return;
    }

    if (packagesWithSelectedProduct.length < this.items.length) {
      const affectedCount = packagesWithSelectedProduct.length;
      const totalCount = this.items.length;
      this.notificationService.info(
        `Free gifts will be added to ${affectedCount} out of ${totalCount} packages that contain the selected product`
      );
    }

    this.checking = true;
    try {
      const result = await this.dataService
        .mutate(CheckBulkAddFreeGiftDocument, {
          input: {
            ids: this.items.map((i) => i.id),
            productId: this.selectedProductId,
          },
        })
        .pipe(take(1))
        .toPromise();

      if (result && result.checkBulkAddFreeGift) {
        this.checkResult = result.checkBulkAddFreeGift;
        this.invalidOrders = result.checkBulkAddFreeGift.invalidOrders || [];

        if (!result.checkBulkAddFreeGift.valid) {
          this.notificationService.warning(
            result.checkBulkAddFreeGift.message || "Invalid operation"
          );
        }
      }
    } catch (error) {
      this.notificationService.error("Failed to check bulk operation");
    } finally {
      this.checking = false;
    }
  }

  public getPackagesContainingProduct(productId: string): any[] {
    return this.items.filter((item) => {
      if (!item.lines) return false;
      return item.lines.some(
        (line: any) =>
          line.productVariant?.productId === productId &&
          !line.productVariant?.product?.customFields?.freeGifts
      );
    });
  }

  private async checkAllAvailableProducts() {
    const productsWithConditions = this.getProductsWithConditions();

    if (productsWithConditions.length === 0) {
      this.checkResult = {
        valid: false,
        message: "No products with conditions found",
        invalidOrders: [],
      };
      return;
    }

    this.checking = true;
    try {
      // Use the new productIds field to check all products in one call
      const result = await this.dataService
        .mutate(CheckBulkAddFreeGiftDocument, {
          input: {
            ids: this.items.map((i) => i.id),
            productIds: productsWithConditions.map((p) => p.id),
          },
        })
        .pipe(take(1))
        .toPromise();

      if (result && result.checkBulkAddFreeGift) {
        this.checkResult = result.checkBulkAddFreeGift;
        this.invalidOrders = result.checkBulkAddFreeGift.invalidOrders || [];

        if (!result.checkBulkAddFreeGift.valid) {
          this.notificationService.warning(
            result.checkBulkAddFreeGift.message || "Invalid operation"
          );
        } else {
          this.notificationService.success("All products checked successfully");
        }
      }
    } catch (error) {
      this.notificationService.error("Failed to check bulk operation");
      this.checkResult = {
        valid: false,
        message: "Failed to check products",
        invalidOrders: [],
      };
    } finally {
      this.checking = false;
    }
  }

  async save() {
    // For single package review with multiple available products, add all products
    if (this.isBulkApplyMode) {
      return this.saveAllAvailableProducts();
    }

    if (!this.selectedProductId) {
      this.notificationService.error("Please select a product");
      return;
    }

    if (!this.checkResult || !this.checkResult.valid) {
      await this.checkBulkOperation();
      if (!this.checkResult || !this.checkResult.valid) {
        return;
      }
    }

    this.loading = true;
    try {
      const result = await this.dataService
        .mutate(BulkAddFreeGiftPackageReviewDocument, {
          input: {
            ids: this.items.map((i) => i.id),
            productId: this.selectedProductId,
          },
        })
        .pipe(take(1))
        .toPromise();

      if (result && result.bulkAddFreeGiftPackageReview) {
        const results = result.bulkAddFreeGiftPackageReview;
        const successCount = results.filter((r: any) => r.success).length;
        const totalGiftsAdded = results.reduce(
          (sum: number, r: any) => sum + r.appliedGifts.length,
          0
        );

        this.notificationService.success(
          `Successfully processed ${successCount} package reviews!`
        );
        this.resolveWith(true);
      }
    } catch (error: any) {
      this.notificationService.error(
        error.message || "Failed to add free gifts"
      );
    } finally {
      this.loading = false;
    }
  }

  private async saveAllAvailableProducts() {
    const productsWithConditions = this.getProductsWithConditions();

    if (productsWithConditions.length === 0) {
      this.notificationService.warning("No products with conditions found");
      return;
    }

    this.loading = true;
    try {
      // Send all product IDs in a single API call
      const result = await this.dataService
        .mutate(BulkAddFreeGiftPackageReviewDocument, {
          input: {
            ids: this.items.map((i) => i.id),
            productIds: productsWithConditions.map((p) => p.id), // Use productIds instead of productId
          },
        })
        .pipe(take(1))
        .toPromise();

      if (result && result.bulkAddFreeGiftPackageReview) {
        const productResults = result.bulkAddFreeGiftPackageReview;

        const successCount = productResults.filter(
          (r: any) => r.success
        ).length;
        const totalGiftsAdded = productResults.reduce(
          (sum: number, r: any) => sum + r.appliedGifts.length,
          0
        );

        if (successCount > 0) {
          this.notificationService.success(
            `Successfully added free gifts for ${productsWithConditions.length} product(s) across ${successCount} package(s).`
          );
          this.resolveWith(true);
        } else {
          this.notificationService.warning(
            "No free gifts were added. Please check product conditions and stock availability."
          );
        }
      } else {
        this.notificationService.warning("No response received from server");
      }
    } catch (error: any) {
      this.notificationService.error(
        error.message || "Failed to add free gifts"
      );
    } finally {
      this.loading = false;
    }
  }

  cancel() {
    this.resolveWith(false);
  }

  getAvailableProductIds(): string[] {
    const allProductIds = new Set<string>();

    this.items.forEach((item) => {
      if (item.lines) {
        item.lines
          .filter(
            (line: any) =>
              !line.productVariant?.product?.customFields?.freeGifts
          )
          .forEach((line: any) => {
            if (line.productVariant?.productId) {
              allProductIds.add(line.productVariant.productId);
            }
          });
      }
    });

    const result = Array.from(allProductIds);
    return result;
  }

  onManualProductSelection(event: any) {
    const productId = event.target.value;

    if (productId) {
      // Find the product in availableProducts
      const product = this.availableProducts.find((p) => p.id === productId);
      if (product) {
        this.selectProduct(product);
      } else {
        this.selectedProductId = productId;
        this.productName = null;
        this.freeGiftSelection = null;
      }
    } else {
      this.selectedProductId = null;
      this.productName = null;
      this.freeGiftSelection = null;
    }
  }

  public getProductsWithConditions() {
    return this.availableProducts.filter((p) => p.hasConditions);
  }

  public getProductsWithoutConditions() {
    return this.availableProducts.filter((p) => !p.hasConditions);
  }

  public toggleProductExpansion(productId: string) {
    // Close currently expanded product if it's different from the clicked one
    if (this.expandedProductId === productId) {
      this.expandedProductId = null;
    } else {
      this.expandedProductId = productId;
    }
  }

  public isProductExpanded(productId: string): boolean {
    return this.expandedProductId === productId;
  }

  public get isBulkApplyMode(): boolean {
    // Enable bulk apply mode when we have products with conditions, regardless of package count
    return this.getProductsWithConditions().length > 1;
  }
}
